import React, { useContext, useRef, useEffect, useState, useMemo } from 'react';
import AppContext from '../contexts/AppContext';
import { Modal } from 'antd';
import { FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';

const DiscoverSpecial = () => {
    const { hotelInfo } = useContext(AppContext);
    const [, setActiveIndex] = useState(0);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [currentMedia, setCurrentMedia] = useState(null);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const videoRefs = useRef([]);

    // 加上权重排序逻辑（高→低，相同随机，空为0）
    const activities = useMemo(() => {
        const list = hotelInfo?.area?.activity || [];
        return [...list].sort((a, b) => {
            const weightA = typeof a.weight === 'number' ? a.weight : 0;
            const weightB = typeof b.weight === 'number' ? b.weight : 0;
            if (weightA !== weightB) return weightB - weightA;
            return Math.random() - 0.5;
        });
    }, [hotelInfo?.area?.activity]);

    const hasActivity = activities.length > 0;

    const bannerImages = useMemo(() => hotelInfo?.area?.bannerImage || [], [hotelInfo?.area?.bannerImage]);
    const bitlyUrl = hotelInfo?.area?.bitlyUrl || null;


    // 自动轮播底部 banner 图片
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    useEffect(() => {
        if (!bannerImages.length) return;
        const interval = setInterval(() => {
            setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [bannerImages]);

    const handleButtonClick = (item) => {
        const type = item.buttonType?.toLowerCase();
        if (type === 'contact us' && item.phoneNumber) {
            window.location.href = `tel:${item.phoneNumber}`;
        } else if (type === 'get directions' && hotelInfo?.landing?.geoLocation) {
            const { latitude, longitude } = hotelInfo.landing.geoLocation;
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
            window.open(mapsUrl, '_blank');
        } else if (type === 'book now' && item.linkUrl) {
            window.open(item.linkUrl, '_blank');
        }
    };

    //  确保所有视频静音后都能启动播放（绕过浏览器自动播放限制）
    useEffect(() => {
        const tryPlayAll = () => {
            videoRefs.current.forEach((video) => {
                if (video) {
                    video.muted = true;
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(() => {
                            // 如果被阻止（第一次渲染），等用户第一次滚动或点击再播放
                            const resumeAll = () => {
                                video.play().catch(() => { });
                                window.removeEventListener('scroll', resumeAll);
                                window.removeEventListener('click', resumeAll);
                            };
                            window.addEventListener('scroll', resumeAll, { once: true });
                            window.addEventListener('click', resumeAll, { once: true });
                        });
                    }
                }
            });
        };

        // 页面加载后尝试启动播放
        const timer = setTimeout(tryPlayAll, 800);
        return () => clearTimeout(timer);
    }, []);


    return (
        <div className="bg-black overflow-visible min-h-screen">
            {/* 活动卡片区域 */}
            {!hasActivity ? (
                <div className="text-center mt-10 text-white">No activity data available.</div>
            ) : (
                <div className="flex flex-col items-center">
                    {activities.map((item, index) => (
                        <div
                            key={index}
                            className="w-full max-w-md overflow-hidden shadow-md border border-black"
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            {/* 标题栏 */}
                            <div className="bg-black pt-3 pb-1 px-4 border-t border-gray-700">
                                <h2 className="text-[16px] font-bold text-white">{item.name}</h2>
                            </div>

                            {/* 视频或图片区域 */}
                            {item.videoUrl ? (
                                <div className="relative bg-black flex justify-center items-center border border-black">
                                    <video
                                        ref={(el) => (videoRefs.current[index] = el)}
                                        src={item.videoUrl}
                                        muted
                                        playsInline
                                        loop
                                        autoPlay
                                        preload="auto"
                                        className="w-full object-cover"
                                        style={{ aspectRatio: '16/9' }}
                                        onClick={() => {
                                            setCurrentMedia(item.videoUrl);
                                            setShowVideoModal(true);
                                        }}
                                        onError={(e) => console.warn('Video load error:', e)}
                                    />

                                </div>
                            ) : item.imageUrls && item.imageUrls.length > 0 ? (
                                <div
                                    className="relative bg-black border border-black"
                                    onClick={() => {
                                        setCurrentMedia(item.imageUrls[0]);
                                        setShowImageModal(true);
                                    }}
                                >
                                    <img
                                        src={item.imageUrls[0]}
                                        alt={item.name}
                                        className="w-full object-cover"
                                        style={{ aspectRatio: '16/9' }}
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-52 bg-gray-200 flex items-center justify-center text-gray-500 border border-black">
                                    No Media
                                </div>
                            )}

                            {/* 按钮 */}
                            {item.buttonType && (
                                <div className="pt-3 pb-4 flex justify-center bg-black">
                                    <button
                                        onClick={() => handleButtonClick(item)}
                                        className="w-[92%] py-[0.1rem] rounded-md text-[12px] font-semibold text-black bg-[#efefef] border border-gray-300 hover:bg-gray-100 transition"
                                    >
                                        {item.buttonType}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {bannerImages.length > 0 && (
                <>
                    {/*  Banner 主体：可展开/收起 */}
                    <div
                        className="fixed left-0 right-0 flex flex-col items-center justify-center bg-white shadow-lg border-t border-gray-200"
                        style={{
                            bottom: '64px', // 在 BottomTabs 上方
                            zIndex: 10,
                            overflow: 'hidden',
                            height: isPanelOpen ? 'calc(100vw * 0.25)' : '0px',
                            transition: 'height 0.6s ease-in-out',
                        }}
                    >
                        {isPanelOpen && (
                            <div
                                className="relative w-full h-full cursor-pointer"
                                onClick={() => {
                                    if (bitlyUrl) window.open(bitlyUrl, '_blank');
                                }}
                            >
                                <AnimatePresence>
                                    <motion.img
                                        key={bannerImages[currentBannerIndex]}
                                        src={bannerImages[currentBannerIndex]}
                                        alt={`banner-${currentBannerIndex}`}
                                        className="w-full h-full object-contain absolute top-0 left-0"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1.2 }}
                                        style={{ backgroundColor: 'white' }}
                                    />
                                </AnimatePresence>
                            </div>
                        )}
                    </div>

                    {/*  使用 React Portal 渲染按钮到 <body> 外层 */}
                    {ReactDOM.createPortal(
                        <button
                            onClick={() => setIsPanelOpen(!isPanelOpen)}
                            className="fixed bg-white/90 px-3 py-1 text-[13px] font-medium transition"
                            style={{
                                bottom: isPanelOpen ? 'calc(64px + calc(100vw * 0.25) - 1px)' : '62px', // 展开时浮在 banner 上方，收起时靠近 BottomTabs
                                right: '0rem',
                                zIndex: 99, //  完全脱离 banner 动画层
                                transition: 'bottom 0.6s ease-in-out',
                            }}
                        >
                            {isPanelOpen ? 'Hide ✕' : 'Show ▲'}
                        </button>,
                        document.body
                    )}
                </>
            )}

            {/* 视频弹窗（灰色透明遮罩 + 自适应方向） */}
            {showVideoModal && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center"
                    style={{
                        width: '100vw',
                        height: '100vh',
                        top: 0,
                        left: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)', // 灰色半透明遮罩
                        backdropFilter: 'blur(1px)', // 轻微模糊提升质感（可选）
                    }}
                >
                    {/* 关闭按钮 */}
                    <FaTimes
                        className="absolute top-4 right-4 text-white text-3xl cursor-pointer z-10"
                        onClick={() => setShowVideoModal(false)}
                    />

                    {/* 视频区域 */}
                    <video
                        src={currentMedia}
                        controls
                        autoPlay
                        playsInline
                        ref={(el) => {
                            if (el) {
                                el.onloadedmetadata = () => {
                                    const ratio = el.videoWidth / el.videoHeight;
                                    // 横屏视频用 contain（左右贴边），竖屏用 cover（上下贴边）
                                    el.style.objectFit = ratio >= 1 ? 'contain' : 'cover';
                                };
                            }
                        }}
                        className="max-w-[100vw] max-h-[95vh]"
                        style={{
                            border: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                        }}
                    />
                </div>
            )}



            <Modal
                open={showImageModal}
                onCancel={() => setShowImageModal(false)}
                footer={null}
                closable={false}
                centered
                width="auto"
                className="custom-fullscreen-modal"
                style={{
                    top: 0,
                    padding: 0,
                    margin: 0,
                }}
                bodyStyle={{
                    padding: 0,
                    background: 'transparent',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                maskStyle={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    backdropFilter: 'blur(1px)',
                }}
            >
                <FaTimes
                    className="absolute top-0 right-0 z-10 cursor-pointer text-white text-3xl"
                    onClick={() => setShowImageModal(false)}
                />
                {currentMedia && (
                    <img
                        src={Array.isArray(currentMedia) ? currentMedia[0] : currentMedia}
                        alt="expanded"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                        }}
                    />
                )}
            </Modal>


        </div>
    );
};

export default DiscoverSpecial;
